import React, { useEffect, useMemo } from 'react';

import ErrorBoundary from '@components/ErrorBoundary';

import ivclass from '@invideoio/web-shared/Modules/IVClass';
import { deviceStateSelector } from '@store/device-state';
import { useAppSelector } from '@store/hook';
import {
  AmplitudeEvents,
  sendAmplitudeAnalyticEvent,
  sendRudderStackAnalyticEvent,
} from '@utils/analytics';
import {
  AnalyticsProductType,
  ClickCtaType,
  ClickCtaUxSource,
} from 'src/types/analytics';

import { localStorageWithExpiry } from '@utils/localStorageUtils';
import { BaseAppType, ToolsRootResponseType } from 'src/types';

import AppPromoBanner from '@components/AppPromoBanner';
import PromotionSection from '@components/Promotion';
import { AUTH_TAB } from '@invideoio/web-shared/components/Auth/constants';
import BannerSlot from '@components/banner-slot';
import Footer from '@invideoio/web-shared/components/Footer';
import Header from '@invideoio/web-shared/components/Header';
import {
  BrowserType,
  PlatformType,
} from '@invideoio/web-shared/constants/common';
import FooterConfig from '@invideoio/web-shared/constants/footer';
import HeaderConfig from '@invideoio/web-shared/constants/header';
import { AppType } from '@invideoio/web-shared/types/Common';
import { BASE_APP_TYPE_LOADED } from '@store/shell-state/shellState';
import { useDispatch } from 'react-redux';
import FeatureCards from '@components/FeatureCard/FeatureCards';
import RootHeroSection from '@components/hero-section/RootHeroSection';
import ToolBox from '@components/ToolBox/toolBox';

interface ToolRootPageContainerProps extends React.PropsWithChildren {
  rootPageState: ToolsRootResponseType;
  user?: any;
  socialLinks?: any;
  browser: BrowserType;
  platform: PlatformType;
}

function ToolRootPageContainer(props: ToolRootPageContainerProps) {
  const { rootPageState, user, socialLinks, browser } = props;

  const deviceState = useAppSelector(deviceStateSelector);

  const { platform, os } = deviceState;

  const {
    HeroSection: heroSection,
    ToolCards: toolCards,
    BottomSection: bottomSection,
    ToolBox1: toolBox1,
    ToolBox2: toolBox2,
    BaseAppType: baseAppType,
  } = rootPageState;

  const dispatch = useDispatch();

  const { HEADER_LINKS } = HeaderConfig;

  const CTAUrl = bottomSection?.CTAUrl;

  const onLogin = () => {
    handleCTAClick({
      ux_source: ClickCtaUxSource.TopRightHeader,
      type: ClickCtaType.ClickLogin,
      tab: AUTH_TAB.LOGIN,
      isAuthClick: true,
    });
  };

  const goToAiSignup = (page?: string) => {
    const qs = localStorageWithExpiry.get('qs');
    const domain_referrer = localStorageWithExpiry.get('domain_referrer');

    let baseURL = `${process.env.NEXT_PUBLIC_AI_HOST}`;

    if (page === 'login') {
      baseURL = baseURL + '/login';
    } else {
      baseURL = baseURL + '/signup';
    }

    if (qs) {
      baseURL = `baseURL${qs}`;
    }
    const url = new URL(baseURL);

    if (domain_referrer) {
      url.searchParams.append('dr', domain_referrer);
    }
    window.location.href = url.toString();
  };

  const handleCTAClick = ({
    ux_source = ClickCtaUxSource.TopRightHeader,
    type = ClickCtaType.ClickSignup,
    btn_position = 'Right Top',
    link = CTAUrl,
    tab = AUTH_TAB.SIGN_UP,
    isAuthClick = false,
  }) => {
    let amplitudeSource = AmplitudeEvents.clickSignUp;

    if (tab === AUTH_TAB.LOGIN) {
      type = ClickCtaType.ClickLogin;
      amplitudeSource = AmplitudeEvents.clickLogin;
    }

    if (isAuthClick) {
      sendAmplitudeAnalyticEvent(amplitudeSource, {
        btnPosition: btn_position,
      });
      sendRudderStackAnalyticEvent({
        event_name: 'click_cta',
        event_properties: {
          ux_source: ux_source,
          type: type,
          feature_utm: 'na',
          product:
            baseAppType === BaseAppType.AI
              ? AnalyticsProductType.AI
              : AnalyticsProductType.STUDIO,
        },
      });
    }

    if (baseAppType === BaseAppType.AI) {
      if (tab === AUTH_TAB.LOGIN) {
        goToAiSignup('login');
      } else {
        goToAiSignup();
      }
      return;
    }

    const url = new URL(
      process.env.NEXT_PUBLIC_STUDIO_HOST || 'https://studio.invideo.io',
    );

    if (!isAuthClick && !link) {
      const url = new URL(
        window.location.pathname + 'editor',
        process.env.NEXT_PUBLIC_TOOLS_HOST,
      );
      window.open(url.toString(), '_self');
    } else {
      if (!link) {
        const linkUrl = new URL(
          window.location.pathname,
          process.env.NEXT_PUBLIC_BASE_HOST,
        );

        if (linkUrl.pathname.startsWith('/templates')) {
          linkUrl.hostname =
            process.env.NEXT_PUBLIC_BASE_HOST || 'https://invideo.io';

          url.searchParams.append('otherUrl', linkUrl.toString());
        } else {
          url.searchParams.append('otherUrl', linkUrl.toString());
        }
      }

      if (tab === AUTH_TAB.LOGIN) {
        url.pathname = '/login';
      } else if (tab === AUTH_TAB.SIGN_UP) {
        url.pathname = '/signup';
      }

      if (btn_position) {
        url.searchParams.append('btn_position', btn_position);
      }

      window.open(url.toString(), '_self');
    }
  };
  const isAiPage = useMemo(() => baseAppType === BaseAppType.AI, [baseAppType]);

  const redirectLogoLink = () => {
    if (isAiPage) {
      return `${process.env.NEXT_PUBLIC_BASE_HOST}/`;
    }
    return !!user
      ? HEADER_LINKS.logoRedirection.loggedInURL
      : HEADER_LINKS.logoRedirection.nonLoggedInURL;
  };

  useEffect(() => {
    dispatch(BASE_APP_TYPE_LOADED(baseAppType ?? BaseAppType.STUDIO));
  }, []);

  return (
    <React.Fragment>
      <ErrorBoundary>
        <AppPromoBanner os={os} platform={platform} />
      </ErrorBoundary>
      <ErrorBoundary>
        {!isAiPage && <BannerSlot browser={browser} />}
      </ErrorBoundary>
      <ErrorBoundary>
        <div
          className={ivclass(
            'xl:iv-max-w-[1440px]',
            'iv-container',
            'iv-mx-auto',
          )}
        >
          <Header
            onLogin={onLogin}
            onSignUp={() =>
              handleCTAClick({
                isAuthClick: true,
              })
            }
            os={os}
            platform={platform}
            appType={AppType.Tools}
            menu={isAiPage ? HeaderConfig.MENU_AI : HeaderConfig.MENU_V2}
            logoRedirectURL={redirectLogoLink()}
            socialLinks={socialLinks}
            newBrandLogo={true}
            logoURL={
              isAiPage
                ? `${process.env.NEXT_PUBLIC_SHARED_CDN_URL}/logos/invideo-ai-light.svg`
                : undefined
            }
            authFunctions={{
              onAILogin: handleCTAClick,
              onAISignUp: handleCTAClick,
              onStudioLogin: handleCTAClick,
              onStudioSignUp: handleCTAClick,
            }}
          />
        </div>
      </ErrorBoundary>

      <hr className={ivclass('iv-border-t iv-border-solid iv-border-grey-5')} />

      {heroSection && (
        <ErrorBoundary>
          <RootHeroSection heroSection={heroSection} />
        </ErrorBoundary>
      )}

      {toolCards && (
        <ErrorBoundary>
          <FeatureCards cards={toolCards} />
        </ErrorBoundary>
      )}

      {toolBox1 && (
        <ErrorBoundary>
          <ToolBox toolBox={toolBox1} />
        </ErrorBoundary>
      )}

      {toolBox2 && (
        <ErrorBoundary>
          <ToolBox toolBox={toolBox2} />
        </ErrorBoundary>
      )}

      {bottomSection && (
        <ErrorBoundary>
          <PromotionSection
            promotionSection={bottomSection}
            platform={platform}
            browser={browser}
            onSignUp={handleCTAClick}
          />
        </ErrorBoundary>
      )}
      <ErrorBoundary>
        {socialLinks && (
          <Footer
            footer={FooterConfig.FOOTER}
            os={os}
            platform={platform}
            socialLinks={socialLinks}
          />
        )}
      </ErrorBoundary>
    </React.Fragment>
  );
}

export default ToolRootPageContainer;
